import type { ClickTweenFunction } from "../types";
import { dotClick } from "./dotClick";
import { gsap } from "gsap";

/**
 * Click Animation: expands the clicked image to fullscreen and allows
 * the user to click again to dismiss it.
 */
export const fullscreenClick: ClickTweenFunction = ({
  imageElement,
  clonedImageRef,
  setIsFullScreen,
}) => {
  if (!imageElement) {
    console.warn("Image element not found, cannot animate.");
    return null;
  }

  // Clone the image and position it fullscreen
  const clone = imageElement.cloneNode(true) as HTMLImageElement;
  clonedImageRef.current = clone;

  gsap.set(clone, {
    position: "fixed",
    top: "50%",
    left: "50%",
    xPercent: -50,
    yPercent: -50,
    width: "100vw",
    height: "100vh",
    objectFit: "contain",
    zIndex: 1000,
    opacity: 0,
    scale: 0.33,
    pointerEvents: "none",
  });
  document.body.appendChild(clone);

  // Exit handler
  const handleCloneClick = (event: MouseEvent) => {
    if (!clonedImageRef.current) return;
    const cloneToRemove = clonedImageRef.current;
    cloneToRemove.removeEventListener("click", handleCloneClick as EventListener); // Cast needed as signature changed

    // Run dotClick animation first
    const dotTimeline = dotClick({ event, imageElement: clonedImageRef.current });

    // Chain the clone removal after dotClick completes
    if (dotTimeline) {
      dotTimeline.eventCallback("onComplete", () => {
        const originalRect = imageElement.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const exitX =
          originalRect.left + originalRect.width / 2 < viewportWidth / 2
            ? `-${viewportWidth}px`
            : `${viewportWidth}px`;

        gsap.to(cloneToRemove, {
          x: exitX,
          opacity: 1,
          duration: 0.3,
          ease: "power1.in",
          onComplete: () => {
            cloneToRemove.remove();
            if (clonedImageRef.current === cloneToRemove) {
              clonedImageRef.current = null;
              setIsFullScreen(false);
            }
          },
        });
      });
    } else {
      // Fallback if dotClick fails (e.g., imageElement missing)
      const originalRect = imageElement.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const exitX =
        originalRect.left + originalRect.width / 2 < viewportWidth / 2
          ? `-${viewportWidth}px`
          : `${viewportWidth}px`;

      gsap.to(cloneToRemove, {
        x: exitX,
        opacity: 1,
        duration: 0.3,
        ease: "power1.in",
        onComplete: () => {
          cloneToRemove.remove();
          if (clonedImageRef.current === cloneToRemove) {
            clonedImageRef.current = null;
            setIsFullScreen(false);
          }
        },
      });
    }
  };

  // Activate click-to-close
  clone.style.pointerEvents = "auto";
  clone.addEventListener("click", (event) => handleCloneClick(event));

  const tl = gsap.timeline();

  // Animate to fullscreen
  tl.to(clone, {
    opacity: 1,
    scale: 1,
    duration: 0.5,
    ease: "power2.out",
    onComplete: () => setIsFullScreen(true),
  });

  return tl
};
